import { useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2, ShieldCheck, ShoppingBag } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { useCart } from "@/context/CartContext";
import { formatBRL } from "@/lib/format";

export default function CartSheet() {
  const { items, open, setOpen, setQty, removeItem, total } = useCart();
  const navigate = useNavigate();

  const checkout = () => {
    setOpen(false);
    navigate("/checkout");
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent
        side="right"
        className="bg-surface border-white/10 w-full sm:max-w-md flex flex-col"
        data-testid="cart-sheet"
      >
        <SheetHeader className="pb-2 border-b border-white/10">
          <SheetTitle className="font-display text-xl" data-testid="cart-sheet-title">
            Seu carrinho
          </SheetTitle>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-4 text-center px-6">
            <ShoppingBag size={40} strokeWidth={1.2} className="text-mute" />
            <p className="text-mute">Seu carrinho está vazio.</p>
            <Button
              variant="outline"
              className="border-white/20 hover:border-cyan hover:text-cyan"
              onClick={() => {
                setOpen(false);
                navigate("/loja");
              }}
              data-testid="cart-empty-explore-button"
            >
              Explorar catálogo
            </Button>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
              {items.map((i) => (
                <div
                  key={i.key}
                  className="flex gap-3 items-center bg-elevated/60 border border-white/10 rounded-md p-3"
                  data-testid={`cart-item-${i.key.replace(/[^a-zA-Z0-9]/g, "-")}`}
                >
                  <img
                    src={i.product?.image_url}
                    alt={i.product?.name}
                    className="h-14 w-14 rounded object-cover bg-elevated"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{i.product?.name}</p>
                    <p className="text-xs text-mute">{formatBRL(i.product?.price)}</p>
                    {i.color && (
                      <span
                        className="inline-block mt-1 text-[10px] uppercase tracking-wide bg-cyan/10 border border-cyan/40 text-cyan px-2 py-0.5 rounded-sm"
                        data-testid="cart-item-color"
                      >
                        {i.color}
                      </span>
                    )}
                    <div className="flex items-center gap-2 mt-2">
                      <button
                        onClick={() => setQty(i.key, i.qty - 1)}
                        className="h-6 w-6 rounded border border-white/15 flex items-center justify-center hover:border-cyan hover:text-cyan transition-colors"
                        aria-label="Diminuir quantidade"
                        data-testid="cart-item-decrease"
                      >
                        <Minus size={12} />
                      </button>
                      <span className="text-sm w-5 text-center" data-testid="cart-item-qty">
                        {i.qty}
                      </span>
                      <button
                        onClick={() => setQty(i.key, i.qty + 1)}
                        className="h-6 w-6 rounded border border-white/15 flex items-center justify-center hover:border-cyan hover:text-cyan transition-colors"
                        aria-label="Aumentar quantidade"
                        data-testid="cart-item-increase"
                      >
                        <Plus size={12} />
                      </button>
                    </div>
                  </div>
                  <button
                    onClick={() => removeItem(i.key)}
                    aria-label="Remover item"
                    className="text-mute hover:text-destructive transition-colors"
                    data-testid="cart-item-remove"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>

            <div className="px-4 pb-5 pt-3 border-t border-white/10 space-y-4 bg-surface">
              <div
                className="flex items-center justify-between text-sm"
                data-testid="cart-free-shipping"
              >
                <span className="text-mute">Frete</span>
                <span className="text-cyan font-medium">Grátis</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-mute">Total</span>
                <span
                  className="font-display text-2xl text-cyan"
                  data-testid="cart-total"
                >
                  {formatBRL(total)}
                </span>
              </div>
              <p className="text-xs text-mute flex items-center gap-2">
                <ShieldCheck size={14} className="text-cyan" />
                Frete grátis para todo o Brasil • Pagamento seguro via Mercado Pago (Pix, cartão ou boleto).
              </p>
              <Button
                onClick={checkout}
                className="w-full h-12 bg-cyan text-ink font-semibold hover:bg-cyan-dark transition-colors text-base"
                data-testid="cart-checkout-button"
              >
                Finalizar compra
              </Button>
              <Button
                variant="ghost"
                className="w-full text-mute hover:text-white"
                onClick={() => setOpen(false)}
              >
                Continuar comprando
              </Button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
